import { useState, useMemo, useCallback, useEffect, useRef, useContext } from 'react';
import { type State, type CountySelector } from '@aidkitorg/types/lib/survey';
import { type County } from '@aidkitorg/types/lib/address/states';
import { Select, Spinner, type SelectOptionType } from "@aidkitorg/component-library";
import { useModularMarkdown } from '../Hooks/ModularMarkdown';
import { generateAccessibleLabel } from '../utils/generateAccessibleLabel';
import InterfaceContext from '../Context';
import { useAsyncEffect } from '../Hooks/AsyncEffect';
import { usePost } from '../API';
import { type DistroQuestionProps } from './Props';

const COUNTIES_BY_STATE: Partial<Record<State, SelectOptionType<County>[]>> = {};

export function CountySelector({
  component,
  info,
  setInfo,
  questionProps,
}: DistroQuestionProps<CountySelector>) {
  const getCountiesForState = usePost('/geo/get_counties_for_state');

  const context = useContext(InterfaceContext);
  const header = useModularMarkdown({ content: component.header ?? '', info });
  const accessibleLabel = component.header
    ? generateAccessibleLabel(component.header[context.lang])
    : '';

  const [isFetchingCounties, setIsFetchingCounties] = useState(false);
  const [options, setOptions] = useState<SelectOptionType<County>[] | undefined>();

  const state = typeof component.state === 'object'
    ? info[component.state.targetField] as State | undefined
    : component.state;
  const previousState = useRef(state);

  const selectedOption = useMemo(() => {
    return options?.find(({ value }) => value.fips_code === info[component.targetField + '_fips_code']);
  }, [options, info[component.targetField + '_fips_code']]);

  const getOptionsForState = useCallback(async (state: State | undefined, abortSignal: AbortSignal) => {
    if (!state) {
      return [];
    }

    if (!COUNTIES_BY_STATE[state]) {
      const response = await getCountiesForState({ state }, abortSignal);
      
      if (!response || ('error' in response)) {
        return [];
      }

      COUNTIES_BY_STATE[state] = response.counties.map((county) => ({
        label: county.county,
        value: county,
      }));
    }

    return COUNTIES_BY_STATE[state];
  }, [getCountiesForState]);

  const handleSelectOption = useCallback((county: SelectOptionType<County> | undefined) => {
    setInfo(component.targetField, county?.value.county ?? '');
    setInfo(component.targetField + '_fips_code', county?.value.fips_code ?? '');
  }, [component.targetField]);

  const customButtonTextRenderer = useMemo(() => {
    return isFetchingCounties
      ? () => <Spinner size='small' align='left' />
      : undefined;
  }, [isFetchingCounties])

  useEffect(() => {
    const stateHasChanged = state !== previousState.current;
    const invalidSelectedOption = !!options && !selectedOption;

    // If the state has changed or we can't find an option matching the saved info,
    // then clear the saved info since it's invalid
    if (stateHasChanged || invalidSelectedOption) {
      handleSelectOption(undefined);
      previousState.current = state;
    }
  }, [state, options, selectedOption, handleSelectOption]);

  useAsyncEffect(async (abortSignal) => {
    setIsFetchingCounties(true);
    setOptions(undefined);
    setOptions(await getOptionsForState(state, abortSignal));
    setIsFetchingCounties(false);
  }, [state, getCountiesForState]);

  if ((questionProps['Additional Options'] || []).indexOf('Hidden') !== -1) {
    return <span></span>;
  }

  return (
    <Select
      options={options}
      value={selectedOption}
      onChange={handleSelectOption}
      disabled={isFetchingCounties || questionProps.blockQuestionEdits}
      customButtonTextRenderer={customButtonTextRenderer}
      description={accessibleLabel}
      label={header}
      virtualized
      searchable
      portal
      clearable
    />
  );
};
